import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { Plus, Search, Edit, Archive, Zap, Calendar, Users, Trash2 } from 'lucide-react';

const SkillAssessmentsPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedYear, setSelectedYear] = useState('all');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [forms, setForms] = useState([]);

    // Form fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [year, setYear] = useState(1);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [skillFormId, setSkillFormId] = useState('');

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        try {
            const [saRes, formsRes] = await Promise.all([
                axios.get(`${API}/skill-assessments`, { headers: getAuthHeaders() }),
                axios.get(`${API}/skill-forms`, { headers: getAuthHeaders() })
            ]);
            setItems(saRes.data);
            setForms(formsRes.data.filter(f => f.status === 'published'));
        } catch { toast.error('Failed to load'); }
        finally { setLoading(false); }
    };

    const openModal = (item = null) => {
        if (item) {
            setEditing(item);
            setTitle(item.title);
            setDescription(item.description || '');
            setYear(item.year);
            setDate(item.date);
            setSkillFormId(item.skill_form_id || '');
        } else {
            setEditing(null);
            setTitle('');
            setDescription('');
            setYear(1);
            setDate(new Date().toISOString().split('T')[0]);
            setSkillFormId('');
        }
        setModalOpen(true);
    };

    const handleSave = async () => {
        if (!title.trim()) return toast.error('Title is required');
        try {
            const payload = { title: title.trim(), description: description.trim() || null, year, date, skill_form_id: skillFormId || null };
            if (editing) {
                await axios.put(`${API}/skill-assessments/${editing.id}`, payload, { headers: getAuthHeaders() });
                toast.success('Updated');
            } else {
                await axios.post(`${API}/skill-assessments`, payload, { headers: getAuthHeaders() });
                toast.success('Created');
            }
            setModalOpen(false);
            fetchAll();
        } catch { toast.error('Failed to save'); }
    };

    const handleArchive = async (item, e) => {
        e.stopPropagation();
        try {
            await axios.post(`${API}/skill-assessments/${item.id}/archive`, {}, { headers: getAuthHeaders() });
            setItems(prev => prev.filter(x => x.id !== item.id));
            toast.success(`"${item.title}" archived`, {
                action: {
                    label: 'Undo', onClick: async () => {
                        try {
                            await axios.post(`${API}/skill-assessments/${item.id}/restore`, {}, { headers: getAuthHeaders() });
                            fetchAll();
                            toast.success('Skill assessment restored');
                        } catch { toast.error('Failed to restore'); }
                    }
                }
            });
        } catch { toast.error('Failed to archive'); }
    };

    const handleDelete = async (item, e) => {
        e.stopPropagation();
        try {
            const res = await axios.delete(`${API}/skill-assessments/${item.id}`, { headers: getAuthHeaders() });
            if (res.data.requires_confirmation) {
                if (!window.confirm(`${res.data.message}\n\nAre you sure you want to permanently delete this skill assessment?`)) return;
                await axios.delete(`${API}/skill-assessments/${item.id}?confirm=true`, { headers: getAuthHeaders() });
            }
            toast.success('Skill assessment deleted permanently');
            setItems(prev => prev.filter(x => x.id !== item.id));
        } catch { toast.error('Failed to delete'); }
    };

    const filtered = items.filter(i => {
        const matchesYear = selectedYear === 'all' || i.year === Number(selectedYear);
        const matchesSearch = i.title.toLowerCase().includes(search.toLowerCase());
        return matchesYear && matchesSearch;
    });

    return (
        <Layout>
            <div className="space-y-6 page-enter" data-testid="skill-assessments-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Skill Assessments</h1>
                        <p className="text-gray-500 mt-1">Assess individual student skills for missed classes</p>
                    </div>
                    <Button onClick={() => openModal()} data-testid="add-skill-assessment-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Skill Assessment
                    </Button>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
                            className="pl-10" data-testid="search-input" />
                    </div>
                    <div className="flex gap-2">
                        {['all', '1', '2', '3'].map(y => (
                            <button key={y} onClick={() => setSelectedYear(y)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedYear === y ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                data-testid={`year-filter-${y}`}>
                                {y === 'all' ? 'All' : `Year ${y}`}
                            </button>
                        ))}
                    </div>
                </div>

                {/* List */}
                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                        <Zap className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No skill assessments found</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table className="w-full ica-table" data-testid="skill-assessments-table">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left">Title</th>
                                    <th className="px-6 py-3 text-left">Year</th>
                                    <th className="px-6 py-3 text-left">Date</th>
                                    <th className="px-6 py-3 text-left">Form</th>
                                    <th className="px-6 py-3 text-left">Students</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filtered.map(item => {
                                    const form = forms.find(f => f.id === item.skill_form_id);
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-50 cursor-pointer transition-colors"
                                            onClick={() => navigate(`/skill-assessments/${item.id}`)} data-testid={`sa-row-${item.id}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-ica-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        <Zap className="w-4 h-4 text-ica-gold" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{item.title}</p>
                                                        {item.description && <p className="text-xs text-gray-500 truncate max-w-[200px]">{item.description}</p>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4"><span className="text-sm bg-ica-navy/10 text-ica-navy px-2 py-0.5 rounded-full font-medium">Year {item.year}</span></td>
                                            <td className="px-6 py-4 text-sm text-gray-600"><Calendar className="w-3.5 h-3.5 inline mr-1" />{item.date}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{form?.name || '—'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600"><Users className="w-3.5 h-3.5 inline mr-1" />{item.enrolled_student_ids?.length || 0}</td>
                                            <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openModal(item); }}
                                                        className="text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold h-8 w-8 p-0" title="Edit">
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={(e) => handleArchive(item, e)}
                                                        className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" data-testid={`archive-sa-${item.id}`} title="Archive">
                                                        <Archive className="w-4 h-4" />
                                                    </Button>
                                                    {isAdmin() && (
                                                        <Button variant="ghost" size="sm" onClick={(e) => handleDelete(item, e)}
                                                            className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" title="Delete Permanently">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Create/Edit Dialog */}
                <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                    <DialogContent className="max-w-md" data-testid="skill-assessment-modal">
                        <DialogTopBar title={<>{editing ? 'Edit' : 'New'} Skill Assessment</>}>
    <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                            <Button size="sm" onClick={handleSave} data-testid="sa-save-btn">{editing ? 'Update' : 'Create'}</Button>
</DialogTopBar>
                        <div className="space-y-4 py-2">
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Knife Skills Makeup" data-testid="sa-title-input" />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                                <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">Year</label>
                                    <select value={year} onChange={e => setYear(Number(e.target.value))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="sa-year-select">
                                        <option value={1}>Year 1</option>
                                        <option value={2}>Year 2</option>
                                        <option value={3}>Year 3</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1 block">Date</label>
                                    <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="sa-date-input" />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Skill Form (Rubric)</label>
                                <select value={skillFormId} onChange={e => setSkillFormId(e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="sa-form-select">
                                    <option value="">— None —</option>
                                    {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default SkillAssessmentsPage;
